import type { ReportSectionMeta } from "~/api/model";
import {
  getEstimatedRemainingMinutes,
  getReportProgress,
  type CompletionObservation,
} from "./reportGenerationProgress";

type ReportEtaTracking = {
  lastDoneCount: number;
  observations: CompletionObservation[];
};

const MAX_TRACKED_REPORTS = 5;
const reportEtaTracking = new Map<string, ReportEtaTracking>();

const getReportTracking = (reportId: string): ReportEtaTracking => {
  const existingTracking = reportEtaTracking.get(reportId);
  if (existingTracking) {
    return existingTracking;
  }

  if (reportEtaTracking.size >= MAX_TRACKED_REPORTS) {
    const oldestReportId = reportEtaTracking.keys().next().value;
    if (oldestReportId) {
      reportEtaTracking.delete(oldestReportId);
    }
  }

  const tracking: ReportEtaTracking = {
    lastDoneCount: 0,
    observations: [],
  };
  reportEtaTracking.set(reportId, tracking);
  return tracking;
};

export const useReportGenerationEta = (
  reportId: string,
  sections: ReadonlyArray<Pick<ReportSectionMeta, "status">>
): number | null => {
  const tracking = getReportTracking(reportId);
  const progress = getReportProgress(sections);
  const doneCount = progress.completedCount + progress.failedCount;

  if (doneCount < tracking.lastDoneCount) {
    tracking.observations = [];
    tracking.lastDoneCount = 0;
  }

  if (doneCount > tracking.lastDoneCount) {
    tracking.observations.push({
      doneCount,
      timestamp: Date.now(),
    });
    tracking.lastDoneCount = doneCount;
  }

  return getEstimatedRemainingMinutes(
    tracking.observations,
    progress.totalCount - progress.completedCount - progress.failedCount
  );
};
